// Service 19 — หนังสือมอบอำนาจส่วนบุคคลและธุรกรรมข้ามประเทศ (Power of Attorney)
// อ้างอิง: ป.พ.พ. บรรพ 3 ลักษณะ 15 ตัวแทน (มาตรา 797–828) และมาตรา 456 เรื่องแบบของนิติกรรมอสังหาริมทรัพย์
// (krisdika.go.th), กรมที่ดิน — แบบ ท.ด.21 หนังสือมอบอำนาจที่ดิน (dol.go.th),
// กรมพัฒนาธุรกิจการค้า (dbd.go.th), กรมสรรพากร — อากรแสตมป์ใบมอบอำนาจ (rd.go.th),
// สภาทนายความในพระบรมราชูปถัมภ์ — ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร (lawyerscouncil.or.th),
// กรมการกงสุล กองสัญชาติและนิติกรณ์ (consular.mfa.go.th),
// HCCH Apostille Convention (hcch.net) — มีผลกับประเทศไทยวันที่ 28 กุมภาพันธ์ 2570
// Client-safe definitions only — no heavy FAQ payload here.

export type PoaFaqCategory =
  | "poa-basics"
  | "property-poa"
  | "bank-financial-poa"
  | "inheritance-poa"
  | "company-poa"
  | "family-child-poa"
  | "notary-signature"
  | "mfa-embassy-chain"
  | "foreign-poa-in-thailand"
  | "revocation-risk";

export interface PoaCategoryDef {
  slug: PoaFaqCategory;
  titleTh: string;
  titleEn: string;
  descTh: string;
  descEn: string;
  matchTh: RegExp;
  matchEn: RegExp;
}

export const POA_FAQ_CATEGORIES: PoaCategoryDef[] = [
  {
    slug: "poa-basics",
    titleTh: "พื้นฐานหนังสือมอบอำนาจและแบบที่ถูกต้อง",
    titleEn: "Power of attorney basics and correct form",
    descTh:
      "ความต่างระหว่างมอบอำนาจทั่วไปกับมอบอำนาจเฉพาะการ ข้อความที่ต้องมีในหนังสือมอบอำนาจ อากรแสตมป์ตามบัญชีอัตราอากรแสตมป์ของกรมสรรพากร และการเลือกภาษาของเอกสารให้ตรงกับหน่วยงานผู้รับ",
    descEn:
      "General versus specific mandates, the clauses a Thai power of attorney must contain, the stamp duty schedule administered by the Revenue Department, and choosing the document language the receiving authority accepts.",
    matchTh: /มอบอำนาจทั่วไป|เฉพาะการ|อากรแสตมป์|แบบฟอร์ม|ข้อความ|ตัวแทน|ผู้รับมอบอำนาจ|ผู้มอบอำนาจ/i,
    matchEn: /general power|specific power|stamp duty|form|clause|agent|attorney-in-fact|principal/i,
  },
  {
    slug: "property-poa",
    titleTh: "มอบอำนาจซื้อขาย โอน จำนอง และเช่าอสังหาริมทรัพย์",
    titleEn: "Property: sale, transfer, mortgage and lease mandates",
    descTh:
      "การใช้แบบ ท.ด.21 ของกรมที่ดิน การมอบอำนาจให้ผู้อื่นไปจดทะเบียนสิทธิและนิติกรรม ณ สำนักงานที่ดิน การระบุเลขโฉนดและเลขห้องชุด และเงื่อนไขเพิ่มเติมเมื่อผู้มอบอำนาจอยู่ต่างประเทศ",
    descEn:
      "Using the Land Department's T.D.21 mandate, appointing someone to register rights and juristic acts at the land office, identifying the title deed or condominium unit precisely, and the extra steps when the principal signs abroad.",
    matchTh: /ที่ดิน|โฉนด|ห้องชุด|คอนโด|จำนอง|โอน|ขายฝาก|เช่า|ท\.ด\.21|สำนักงานที่ดิน/i,
    matchEn: /land|title deed|condominium|mortgage|transfer|lease|land office|T\.D\.21|conveyanc/i,
  },
  {
    slug: "bank-financial-poa",
    titleTh: "มอบอำนาจด้านธนาคาร ภาษี และการเงิน",
    titleEn: "Banking, tax and financial mandates",
    descTh:
      "การมอบอำนาจปิดบัญชี ถอนเงิน จัดการกองทุนหรือหลักทรัพย์ ยื่นแบบภาษี และรับเงินคืนภาษี รวมถึงเหตุผลที่ธนาคารแต่ละแห่งมีแบบฟอร์มของตนเองและมักไม่รับหนังสือมอบอำนาจทั่วไป",
    descEn:
      "Mandates to close accounts, withdraw funds, manage funds or securities, file tax returns and collect refunds — and why each bank keeps its own form and rarely accepts a generic mandate.",
    matchTh: /ธนาคาร|บัญชี|ถอนเงิน|กองทุน|หลักทรัพย์|ภาษี|คืนภาษี|ประกันสังคม|บำนาญ/i,
    matchEn: /bank|account|withdraw|fund|securities|tax|refund|social security|pension/i,
  },
  {
    slug: "inheritance-poa",
    titleTh: "มอบอำนาจเรื่องมรดก ทายาท และผู้จัดการมรดก",
    titleEn: "Estate, heirs and administrator mandates",
    descTh:
      "การมอบอำนาจให้ญาติในไทยยื่นคำร้องตั้งผู้จัดการมรดก รับโอนทรัพย์มรดก หรือรับเงินจากสถาบันการเงิน และเอกสารประกอบอย่างมรณบัตร ทะเบียนบ้าน และหลักฐานความเป็นทายาท",
    descEn:
      "Authorising a relative in Thailand to petition for an estate administrator, receive inherited assets or collect funds from financial institutions, plus the supporting death certificate, house registration and proof of heirship.",
    matchTh: /มรดก|ทายาท|ผู้จัดการมรดก|มรณบัตร|พินัยกรรม|โอนมรดก/i,
    matchEn: /estate|heir|administrator|death certificate|will|probate|inherit/i,
  },
  {
    slug: "company-poa",
    titleTh: "มอบอำนาจในนามนิติบุคคลและกรรมการ",
    titleEn: "Corporate and director mandates",
    descTh:
      "การมอบอำนาจโดยกรรมการผู้มีอำนาจตามหนังสือรับรองนิติบุคคลของกรมพัฒนาธุรกิจการค้า การแนบหนังสือรับรองและตราประทับ ขอบเขตอำนาจที่ควรจำกัด และการมอบอำนาจช่วง",
    descEn:
      "Mandates signed by directors named in the DBD company affidavit, attaching the affidavit and company seal, keeping the scope narrow, and sub-delegation.",
    matchTh: /นิติบุคคล|บริษัท|กรรมการ|หนังสือรับรองบริษัท|ตราประทับ|มอบอำนาจช่วง|บอจ|ห้างหุ้นส่วน/i,
    matchEn: /company|corporate|director|affidavit|company seal|sub-delegat|partnership|board resolution/i,
  },
  {
    slug: "family-child-poa",
    titleTh: "มอบอำนาจเรื่องครอบครัว บุตร และหนังสือยินยอม",
    titleEn: "Family, children and consent letters",
    descTh:
      "หนังสือยินยอมให้บุตรเดินทางออกนอกประเทศ หนังสือยินยอมให้ทำหนังสือเดินทาง การมอบอำนาจเรื่องทะเบียนราษฎร และการมอบอำนาจดำเนินการแทนคู่สมรสที่อยู่ต่างประเทศ",
    descEn:
      "Consent for a child to travel or to be issued a passport, mandates for civil-registration errands, and acting for a spouse who is overseas.",
    matchTh: /ยินยอม|บุตร|เด็ก|หนังสือเดินทาง|ทะเบียนราษฎร|คู่สมรส|ปกครองบุตร|อำนาจปกครอง/i,
    matchEn: /consent|child|minor|passport|civil registration|spouse|custody|parental/i,
  },
  {
    slug: "notary-signature",
    titleTh: "การรับรองลายมือชื่อและพยานในหนังสือมอบอำนาจ",
    titleEn: "Signature certification and witnesses",
    descTh:
      "บทบาทของทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร (Notarial Services Attorney) ตามข้อบังคับสภาทนายความ การลงนามต่อหน้าพยานสองคน และการรับรองลายมือชื่อของชาวต่างชาติในไทย",
    descEn:
      "The role of a Notarial Services Attorney registered with the Lawyers Council of Thailand, signing before two witnesses, and certifying a foreign national's signature in Thailand.",
    matchTh: /ลายมือชื่อ|โนตารี|ทนายความผู้ทำคำรับรอง|พยาน|ลงนามต่อหน้า|notarial/i,
    matchEn: /signature|notary|notarial|witness|signed before|attesting/i,
  },
  {
    slug: "mfa-embassy-chain",
    titleTh: "ลำดับรับรองกรมการกงสุล สถานทูต และ Apostille",
    titleEn: "MFA, embassy and Apostille chain",
    descTh:
      "ลำดับชั้นรับรองหนังสือมอบอำนาจที่จะใช้ต่างประเทศ ตั้งแต่รับรองลายมือชื่อ คำแปล การรับรองที่กองสัญชาติและนิติกรณ์ ไปจนถึงสถานทูตปลายทาง และการเปลี่ยนผ่านไปสู่ Apostille",
    descEn:
      "The certification chain for an outbound mandate: signature certification, translation, the Legalisation Division at the Department of Consular Affairs, the destination embassy, and the coming switch to the Apostille.",
    matchTh: /กรมการกงสุล|นิติกรณ์|สถานทูต|Apostille|2570|แจ้งวัฒนะ|รับรองคำแปล/i,
    matchEn: /consular affairs|legalisation|legalization|embassy|apostille|2027|MFA/i,
  },
  {
    slug: "foreign-poa-in-thailand",
    titleTh: "หนังสือมอบอำนาจจากต่างประเทศที่จะใช้ในไทย",
    titleEn: "Foreign mandates used inside Thailand",
    descTh:
      "เอกสารที่ลงนามหน้า notary ต่างประเทศแล้วต้องผ่านอะไรก่อนใช้ในไทย การแปลเป็นไทย การรับรองคำแปลที่กรมการกงสุล และข้อกำหนดของสำนักงานที่ดิน ธนาคาร และศาลไทย",
    descEn:
      "What a document notarised abroad must pass through before a Thai authority accepts it: Thai translation, MFA certification of the translation, and what land offices, banks and courts each require.",
    matchTh: /ต่างประเทศลงนาม|notary ต่างประเทศ|สถานทูตไทย|แปลเป็นไทย|ใช้ในไทย|นำเข้ามาใช้/i,
    matchEn: /notarised abroad|notarized abroad|Thai embassy|translate into Thai|use in Thailand|inbound/i,
  },
  {
    slug: "revocation-risk",
    titleTh: "การเพิกถอน ความเสี่ยง และข้อควรระวัง",
    titleEn: "Revocation, risk and cautions",
    descTh:
      "การบอกเลิกหรือเพิกถอนการมอบอำนาจ ผลเมื่อผู้มอบอำนาจถึงแก่ความตายหรือไร้ความสามารถ อายุการใช้งานที่หน่วยงานยอมรับ ความเสี่ยงจากการเว้นช่องว่างในเอกสาร และวิธีจำกัดขอบเขตอำนาจ",
    descEn:
      "Revoking a mandate, what happens if the principal dies or loses capacity, how recent a mandate must be, the risk of blank spaces, and how to keep the scope tight.",
    matchTh: /เพิกถอน|บอกเลิก|หมดอายุ|เสียชีวิต|ไร้ความสามารถ|ช่องว่าง|ความเสี่ยง|ตีกลับ|ปฏิเสธ/i,
    matchEn: /revoke|revocation|terminate|expire|dies|incapacity|blank|risk|rejected|refused/i,
  },
];

export function getPoaCategoryDef(slug: string): PoaCategoryDef | undefined {
  return POA_FAQ_CATEGORIES.find((c) => c.slug === slug);
}